<template>
  <div class="flex flex-wrap items-center gap-2" data-test="answer-condition">
    <Select
      v-if="question"
      :model-value="condition.operator"
      :options="operators"
      option-label="label"
      option-value="value"
      aria-label="Answer operator"
      @update:model-value="selectOperator" />
    <template v-if="question && answerNeedsValue(condition.operator) && valueType !== 'presence'">
      <template v-if="valueType === 'number' || valueType === 'date'">
        <template v-for="index in condition.operator === 'between' ? [0, 1] : [0]" :key="index">
          <span v-if="index === 1">and</span>
          <InputNumber
            v-if="valueType === 'number'"
            :model-value="numericValue(index)"
            :use-grouping="false"
            :min="isRelativeDays ? 0 : undefined"
            :max-fraction-digits="isRelativeDays ? 0 : 20"
            :aria-label="
              isRelativeDays
                ? 'Number of days'
                : condition.operator === 'between'
                  ? index === 0
                    ? 'Minimum answer'
                    : 'Maximum answer'
                  : 'Answer value'
            "
            @update:model-value="updateValue($event ?? undefined, index)" />
          <InputText
            v-else
            type="date"
            :model-value="textValue(index)"
            :aria-label="condition.operator === 'between' ? (index === 0 ? 'Start date' : 'End date') : 'Answer date'"
            @update:model-value="updateValue($event, index)" />
        </template>
      </template>
      <MultiSelect
        v-else-if="valueType === 'country' && isMultiple"
        :model-value="Array.isArray(condition.value) ? condition.value : []"
        :options="countryOptions"
        option-label="label"
        option-value="value"
        display="chip"
        filter
        aria-label="Answer countries"
        @update:model-value="updateValue($event)" />
      <Select
        v-else-if="valueType === 'country'"
        :model-value="condition.value"
        :options="countryOptions"
        option-label="label"
        option-value="value"
        filter
        placeholder="Select a country"
        aria-label="Answer country"
        @update:model-value="updateValue($event)" />
      <AutoComplete
        v-else-if="condition.operator === 'domain is any of'"
        :model-value="Array.isArray(condition.value) ? condition.value : []"
        multiple
        :typeahead="false"
        aria-label="Answer domains"
        placeholder="Add a domain and press Enter"
        @update:model-value="updateValue($event)" />
      <InputText
        v-else
        :model-value="textValue(0)"
        :aria-label="isDomain ? 'Answer domain' : 'Answer text'"
        :placeholder="isDomain ? 'example.com' : 'Answer text'"
        @update:model-value="updateValue($event)" />
    </template>
    <small v-if="!question" class="w-full text-slate-500">Question no longer available</small>
    <small v-for="error in errors" :key="error.field" class="w-full text-red-600" role="alert">{{
      error.message
    }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import MultiSelect from 'primevue/multiselect'
import AutoComplete from 'primevue/autocomplete'
import phoneCountries from 'intl-tel-input/data'
import countries from '@dashboard/data/countries'
import type { AnswerCondition, AnswerConditionOperator } from '@dashboard/types/config'
import { useQuestionStore } from '../stores/questionStore'
import {
  answerNeedsValue,
  answerOperators,
  answerValueType,
  validateAnswerCondition
} from '../services/answerConditions'

const props = defineProps<{ condition: AnswerCondition }>()
const emit = defineEmits<{ 'update:condition': [condition: AnswerCondition] }>()
const questionStore = useQuestionStore()
const question = computed(() =>
  questionStore.questions.find((item) => item.id === props.condition.questionId && item.displayType === 'input')
)
const valueType = computed(() => answerValueType(question.value?.inputType, props.condition.operator))
const operators = computed(() =>
  answerOperators(question.value?.inputType, question.value).map((value) => ({ label: value, value }))
)
const isDomain = computed(() => props.condition.operator.startsWith('domain '))
const isRelativeDays = computed(() =>
  ['within the next days', 'within the last days'].includes(props.condition.operator)
)
const isMultiple = computed(() =>
  ['is any of', 'country is any of', 'domain is any of'].includes(props.condition.operator)
)
const dialCodes: Record<string, string> = Object.fromEntries(
  phoneCountries.map((country) => [country.iso2.toUpperCase(), country.dialCode])
)
const countryOptions = computed(() =>
  question.value?.inputType === 'phone'
    ? countries.flatMap((country) => {
        const dialCode = dialCodes[country.value]
        return dialCode ? [{ ...country, label: `${country.label} (+${dialCode})` }] : []
      })
    : countries
)
const errors = computed(() => validateAnswerCondition(props.condition, questionStore.questions))
const rangeDraft = ref<(string | number | undefined)[]>([])
watch(
  [() => props.condition.questionId, () => props.condition.operator],
  () => {
    rangeDraft.value = Array.isArray(props.condition.value) ? [...props.condition.value] : []
  },
  { immediate: true }
)
watch(
  () => props.condition.value,
  (value) => {
    if (Array.isArray(value)) {
      rangeDraft.value = [...value]
    }
  }
)

function selectOperator(operator: AnswerConditionOperator) {
  const next: AnswerCondition = { $source: 'answers', questionId: props.condition.questionId, operator }
  const nextValueType = answerValueType(question.value?.inputType, operator)
  if (answerNeedsValue(operator)) {
    if (['is any of', 'country is any of', 'domain is any of'].includes(operator)) {
      next.value = isMultiple.value && nextValueType === valueType.value ? props.condition.value : []
    } else if (operator === 'between') {
      // A missing range is shown as two empty fields until both endpoints are entered.
      next.value = nextValueType === 'date' ? ['', ''] : undefined
    } else if (
      answerNeedsValue(props.condition.operator) &&
      nextValueType === valueType.value &&
      !Array.isArray(props.condition.value)
    ) {
      next.value = props.condition.value
    } else if (nextValueType === 'text') {
      next.value = ''
    }
  }
  emit('update:condition', next)
}

function numericValue(index: number): number | undefined {
  const value = props.condition.operator === 'between' ? rangeDraft.value[index] : props.condition.value
  return typeof value === 'number' ? value : undefined
}

function textValue(index: number): string {
  const value = props.condition.operator === 'between' ? rangeDraft.value[index] : props.condition.value
  return typeof value === 'string' ? value : ''
}

function updateValue(value: string | number | string[] | undefined, index = 0) {
  if (props.condition.operator === 'between') {
    // Keep incomplete endpoints local to the editor; only complete tuples enter config.
    rangeDraft.value[index] = typeof value === 'string' || typeof value === 'number' ? value : undefined
    const [minimum, maximum] = rangeDraft.value
    const range: AnswerCondition['value'] =
      typeof minimum === 'number' && typeof maximum === 'number'
        ? [minimum, maximum]
        : typeof minimum === 'string' && typeof maximum === 'string'
          ? [minimum, maximum]
          : undefined
    emit('update:condition', { ...props.condition, value: range })
    return
  }
  emit('update:condition', { ...props.condition, value })
}
</script>
